/* eslint-disable max-lines */
import { comparer } from 'mobx';
import { AnalyticsStore, ContextStore, ExperimentStore, PageStore } from 'bernie-plugin-mobx';

import { AdditionalMeta, PageData, PaginationAttribute } from 'bernie-http';
import { codeSplit, Controller, FetchOptions, FetchPageOptions, merge, SerializedData } from 'bernie-core';
import { formatText, Localization } from 'bernie-l10n';
import { LocationReactorResult } from 'bernie-client';

import { evaluateAndLogExperiment } from 'stores/utils';
import { getPackageType, getSearchTimeout, shouldCallMultiItemSessionMutation } from 'stores/multi-item-store-util';
import { getSearchImpactingKeys, PropertySort, SearchCriteriaModel, singleUseQueryParams, untrackedQueryParams } from 'stores/models';
import { MultiItemStore } from 'stores/multi-item-store';
import { PageLayoutStore } from 'src/common/stores/page-layout-store';
import { SearchCriteriaUtils } from 'stores/models/search-criteria-utils';
import { SearchQueryAdapter } from 'stores/adapters/search-query-adapter';
import { SearchResultsStore } from 'stores/search-results-store';

import { PackageType } from 'models/multi-item/multi-item-types';
import { SEARCH_PAGE_EG_CLICKSTREAM_NAME, SearchClickstreamPageNameDetailed, SearchPageName } from 'constants/pages';

import { getAdBlockerEventParams, isInBrowser } from 'components/meso/utils';
import { getTrackingInfoForLogging } from 'utils/logging-utils';
import { meta } from 'utils/meta';
import { processClientSideDefaultRequestFields } from './shared-controller-modules/utils';

import { CONTROLLER_EVENTS } from 'controllers/events/controller-events';
import { convertHashParams, shouldHashOverrideDefaultValues, shouldSearchHashParams } from './converters/legacy-hash-params';

const { withoutKeys, withKeys } = merge;

const CHINA_POS_SITE_ID = 100001;

export class SearchController implements Controller {
  public bundles = ['search'];
  public component = codeSplit(() => import(/* webpackChunkName: "search" */ 'views/hotel/search.tsx') as any);
  public pageId = `${SearchPageName.SA},H,20`;
  public path = '/(Hotel-Search|search.do)*';
  public exact = true;
  public routeName = 'search';
  public routeData = { rfrrNamespace: 'HOT.SR', pageName: 'page.Hotel-Search' };

  // eslint-disable-next-line complexity
  public async fetch(options: FetchOptions): Promise<any> {
    if (!options || !options.stores) {
      return Promise.reject('SearchController.fetch receieved an invalid StoreContainer');
    }

    const { isServer, query, stores, logger } = options;
    const fetchStartTime = Date.now();
    const context: ContextStore = stores.get<ContextStore>('context');
    const page: PageStore = stores.get<PageStore>('page');
    const analyticsStore: AnalyticsStore = stores.get<AnalyticsStore>('analytics');
    const searchCriteria: SearchCriteriaModel = stores.get<SearchCriteriaModel>('searchCriteria');
    const searchResults: SearchResultsStore = stores.get<SearchResultsStore>('searchResults');
    const multiItem: MultiItemStore = stores.get<MultiItemStore>('multiItem');
    const experiments: ExperimentStore = stores.get<ExperimentStore>('experiment');
    const pageLayoutStore: PageLayoutStore = stores.get<PageLayoutStore>('pageLayout');

    // sanitize incoming params
    const singleUseCriteria: any = withKeys(singleUseQueryParams).merge({}, options.query);
    const untrackedKeys = [...untrackedQueryParams, ...singleUseCriteria, 'prevQuery', 'l10n'];
    const prevQuery = withoutKeys(untrackedKeys).merge({}, query.prevQuery);
    const nextQuery = withoutKeys(untrackedKeys).merge({}, query);
    const packageType = getPackageType(query);

    this.initializeMultiItem(multiItem, query, nextQuery, context);

    searchResults.pageData = page;

    if (searchResults.foregoClientFetch) {
      searchResults.foregoClientFetch = false;
      const criteria = searchResults.searchCriteria.toJSON();
      const sqaParsedOut = SearchQueryAdapter.parseOut(criteria);

      return { query: sqaParsedOut, replace: true } as LocationReactorResult;
    }
    // if client side detects hash params, handle them
    if (!isServer && shouldSearchHashParams(nextQuery)) {
      const hashParams = convertHashParams();

      if (shouldHashOverrideDefaultValues(hashParams)) {
        searchResults.defaultRequestFields = {};
      }
      if (hashParams) {
        return { query: hashParams, replace: true } as LocationReactorResult;
      }
    }
    // server rendered with defaulted params, we need to adjust URLs first
    let newQuery = processClientSideDefaultRequestFields(isServer, searchResults, nextQuery);
    if (newQuery) {
      return newQuery;
    }

    const pageLayoutTemplatePromise = pageLayoutStore.fetchSRP(context, searchCriteria.destination);

    const forceFetch = searchResults.shouldFetch;
    // check if queries are different to previous state
    if (forceFetch || !comparer.structural(nextQuery, prevQuery)) {
      // queries are meaningfully diff, setup base stores again
      searchCriteria.setValues({
        ...SearchCriteriaModel.getDefaults(),
        ...SearchCriteriaUtils.getFilterDefaults(),
        ...nextQuery,
      });

      const modelData = SearchQueryAdapter.parseIn(searchCriteria.toJSON());
      const incomingSearchCriteria = new SearchCriteriaModel(modelData, new Localization(context.locale));

      // fetches are only carried out if criteria is also changed
      if (forceFetch || this.hasStoresChanged(searchResults.searchCriteria, incomingSearchCriteria)) {
        if (isServer === true && query && multiItem.getPackageType() === PackageType.FH) {
          const bucket = evaluateAndLogExperiment('MIS_Lodging_SRP_Enable_Client_Side_Rendering_', context, experiments, page);
          query.skipServerFetch = bucket === 1 ? 'true' : query.skipServerFetch;
        }
        const skipSearchResultsFetch = isServer === true && query && query.skipServerFetch === 'true';

        const isMapDeeplinkFetch = isServer && query.pwaOverlay === 'map';
        const returnPropertyType = !isServer || isMapDeeplinkFetch;

        await this.doNetworking(
          page,
          analyticsStore,
          context,
          searchResults,
          incomingSearchCriteria,
          skipSearchResultsFetch,
          singleUseCriteria,
          searchCriteria,
          multiItem,
          packageType,
          experiments,
          returnPropertyType
        )
          .then(() => {
            if (logger) {
              logger.logEvent(
                CONTROLLER_EVENTS.SEARCH_CONTROLLER_FETCH,
                `fetchTime=${(Date.now() - fetchStartTime).toString()} ms`,
                `, isServer=${isServer}, ${getTrackingInfoForLogging(context)}`
              );
            }
          })
          .catch(error => {
            if (logger) {
              logger.logEvent(CONTROLLER_EVENTS.SEARCH_CONTROLLER_FETCH_FAILURE, error);
            }
          });
      }
    }

    if (!isServer) {
      this.sendAdBlockerEvent(analyticsStore);
    }

    await pageLayoutTemplatePromise;
    // cleanup defaultRequestFields so they don't hang around for subsequent calls on clientside
    newQuery = processClientSideDefaultRequestFields(isServer, searchResults, nextQuery);
    if (newQuery) {
      return newQuery;
    }

    const destinationTitle = searchCriteria.destination || searchResults.searchedDestination;
    // `formatText` is deprecated. We should have the api create the localized page title and send it to the client.
    const pageTitle = destinationTitle
      ? formatText('hotels.searchResults.successfulSearch.pageTitle', destinationTitle)
      : formatText('hotels.searchResults.invalidSearch.pageTitle');
    page.title = pageTitle;
    // `document` is undefined on the server only set `document.title` on the client.
    if (!isServer) {
      document.title = pageTitle;
    }
  }

  private initializeMultiItem(multiItem: MultiItemStore, query: SerializedData, nextQuery: SerializedData, context: ContextStore) {
    multiItem.buildContext(query);
    multiItem.referrer = query.rfrr;

    if (query.multiProp === 'true' && !query.businessModelFilter && nextQuery) {
      const defaultBusinessModel = multiItem.getDefaultBusinessModel();
      if (multiItem.cartInfo && multiItem.cartInfo.identifier === 'mixAndMatch') {
        multiItem.removeMixAndMatchCart();
        multiItem.getOrCreateCart(context, defaultBusinessModel);
      }
      nextQuery.businessModelFilter = defaultBusinessModel;
    }
  }

  private async doNetworking(
    page: PageStore,
    analyticsStore: AnalyticsStore,
    context: ContextStore,
    searchResults: SearchResultsStore,
    incomingSearchCriteria: SearchCriteriaModel,
    skipServerFetch: boolean,
    singleUseCriteria: SerializedData,
    searchCriteria: SearchCriteriaModel,
    multiItem: MultiItemStore,
    packageType: PackageType,
    experiments?: ExperimentStore,
    returnPropertyType?: boolean
  ) {
    const pageFetch = page.fetch(meta['hotel-search'], context.site, context.locale);

    if (
      multiItem &&
      multiItem.isPackageShoppingPath() &&
      !multiItem.isServerSide() &&
      shouldCallMultiItemSessionMutation(searchResults.searchCriteria, incomingSearchCriteria, multiItem.getPackageType())
    ) {
      await searchResults.createSessionBeforeMultiItemFetch(multiItem, context, incomingSearchCriteria);

      // Setting the regionId to null so that correct regionId is retrieved in spinner for multicity searches.
      if (searchResults.searchCriteria.regionId !== incomingSearchCriteria.regionId) {
        incomingSearchCriteria.regionId = null;
      }
    }

    // Add an exception to allow for the filters to be updated if overfiltered
    // Add another expection to allow searching when the previous search was invalid
    if (!searchResults.isError || searchResults.overFiltered || this.previousSearchWasInvalid(searchResults)) {
      searchResults.updateSearchCriteria(incomingSearchCriteria);
      const searchResultsFetch = skipServerFetch
        ? Promise.resolve()
        : searchResults.fetch(
            analyticsStore,
            context,
            getSearchTimeout(packageType),
            singleUseCriteria,
            multiItem,
            page,
            experiments,
            returnPropertyType
          );

      await pageFetch;
      await searchResultsFetch;

      this.updateSearchCriteriaFromSearchResults(searchResults, searchCriteria);
    }
  }

  public fetchPageData(options: FetchPageOptions): Promise<PageData> {
    const additionalMeta: AdditionalMeta[] = this.addResourceHints(options.context);

    const seo = {
      seo: {
        ...(options.context?.site?.id !== CHINA_POS_SITE_ID && { additionalMeta }),
        // Temporary fix for issue: https://jira.expedia.biz/browse/BHS-17025
        robots: 'noindex,nofollow',
      },
    };

    const packageType = getPackageType(options.query);
    const pageData: Partial<PageData> = packageType
      ? this.getPackagePageData(packageType)
      : {
          navigationId: 'hotel',
          clickstreamPageName: SEARCH_PAGE_EG_CLICKSTREAM_NAME,
          clickstreamPageNameDetailed: SearchClickstreamPageNameDetailed.SA,
        };

    return Promise.resolve({
      ...seo,
      ...pageData,
    }) as Promise<PageData>;
  }

  private addResourceHints(context) {
    return [new PaginationAttribute(context?.deviceInformation?.mobile ? 'dns-prefetch' : 'preconnect', 'https://maps.googleapis.com/')];
  }

  private getPackagePageData(packageType: PackageType) {
    const pageName = SearchPageName[packageType];

    return {
      pageName,
      pageId: `${pageName},P,20`,
      navigationId: 'package',
      clickstreamPageName: SEARCH_PAGE_EG_CLICKSTREAM_NAME,
      clickstreamPageNameDetailed: SearchClickstreamPageNameDetailed[packageType],
    };
  }

  private updateSearchCriteriaFromSearchResults(searchResults, searchCriteria) {
    // BFF may provide regionId when coming from SEM or alternate dates
    if (searchResults.searchCriteria) {
      searchCriteria.startDate = searchResults.searchCriteria.startDate;
      searchCriteria.endDate = searchResults.searchCriteria.endDate;
      searchCriteria.regionId = searchResults.searchCriteria.regionId;
      searchCriteria.typeaheadRegionType = searchResults.searchCriteria.typeaheadRegionType;
    }
  }

  private getComparableKeys(prevStore: SearchCriteriaModel, newStore: any) {
    let compareKeys = getSearchImpactingKeys();
    if (!this.didGeoChange(prevStore, newStore)) {
      compareKeys = compareKeys.filter(key => key !== 'regionId' && key !== 'latLong' && key !== 'destination');
    }

    // If we're asking to sort by values that's equivalent to what we currently have,
    // remove sort field from the comparison to avoid another service call
    const askingForEmptySortWhileWeHaveRecommended = newStore.sort === '' && prevStore.sort === PropertySort.RECOMMENDED;
    const askingForMctc2SortWhileWeHaveDeals = !newStore.sort && newStore.mctc === '2' && prevStore.sort === 'deals';
    const isSameSortParam = newStore.sort && prevStore.sort && newStore.sort.toLowerCase() === prevStore.sort.toLowerCase();
    const equivalentSort = askingForEmptySortWhileWeHaveRecommended || askingForMctc2SortWhileWeHaveDeals || isSameSortParam;

    if (equivalentSort) {
      compareKeys = compareKeys.filter(key => key !== 'sort');
    }

    return compareKeys;
  }

  private didGeoChange(prevStore: SearchCriteriaModel, newStore: any) {
    const { regionId: prevRegionId, latLong: prevLatLong, destination: prevDestination } = prevStore;
    const { regionId: newRegionId, latLong: newLatLong, destination: newDestination } = newStore;

    const isNewRegionIdEmpty = newRegionId === '';
    const isNewLatLongEmpty = newLatLong === '';
    const isNewDestinationEmpty = newDestination === '';
    const didRegionIdChange = !isNewRegionIdEmpty && newRegionId !== prevRegionId;
    const didLatLongChange = !isNewLatLongEmpty && newLatLong !== prevLatLong;
    const didDestinationChange = !isNewDestinationEmpty && newDestination !== prevDestination;

    return didRegionIdChange || (isNewRegionIdEmpty && (didLatLongChange || (isNewLatLongEmpty && didDestinationChange)));
  }

  private hasStoresChanged(prevStore: SearchCriteriaModel, newStore: SearchCriteriaModel) {
    if (Boolean(newStore) && !prevStore) {
      return true;
    }

    return SearchCriteriaUtils.hasSearchCriteriaChanged(prevStore, newStore, this.getComparableKeys(prevStore, newStore));
  }

  private previousSearchWasInvalid(searchResults: SearchResultsStore) {
    // Will return true if the previous search contained an invalid location
    return searchResults.isError && searchResults.isEmpty && searchResults.searchCriteria.regionId === '';
  }

  private sendAdBlockerEvent(analyticsStore: AnalyticsStore): void {
    if (isInBrowser()) {
      const { referrer, linkName } = getAdBlockerEventParams();
      analyticsStore.trackEvent(referrer, linkName, null);
    }
  }
}
